import { useMemo } from "react";
import { getI18n } from "react-i18next";
import { centsToValue } from "./currency.precision";

type UseFormatterRawOptions = {
  precision: number;
  currencyCode: string;
};

type UseFormatterAssetOptions = {
  asset: string;
};

const currencyListToSymbol = {
  EUR: "€",
  GBP: "£",
  BGN: "лв",
  HRK: "kn",
  CZK: "Kč",
  DKK: "kr",
  GIP: "£",
  HUF: "Ft",
  ISK: "kr",
  CHF: "CHF",
  NOK: "kr",
  PLN: "zł",
  RON: "lei",
  SEK: "kr",
};

export type UseFormatterOptions =
  | UseFormatterRawOptions
  | UseFormatterAssetOptions;

function parseRawOptions(options: UseFormatterOptions): UseFormatterRawOptions {
  if ("asset" in options) {
    const components = options.asset.split("/");

    if (components.length < 2) {
      console.error(
        "Unable to parse currencyCode + precision for asset ",
        options.asset
      );
      return {
        currencyCode: options.asset,
        precision: 2,
      };
    }

    return {
      precision: parseInt(components[1]),
      currencyCode: components[0],
    };
  }

  return options;
}

export function useSumFormatter(options: UseFormatterOptions) {
  const { precision, currencyCode } = parseRawOptions(options);
  const locale = getI18n().language;
  const [sumFormatter, currencySymbol] = useMemo(() => {
    const format = new Intl.NumberFormat(locale, {
      currency: currencyCode,
      style: "currency",
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    });

    // formatToParts is not implemented on iOS.
    // Fallback to currency code
    let symbol: string | undefined = currencyCode;

    if (typeof format.formatToParts === "function") {
      symbol = format
        .formatToParts(1)
        .find((p) => p.type === "currency")?.value;
    } else {
      symbol =
        currencyListToSymbol[currencyCode as keyof typeof currencyListToSymbol];
    }

    return [format, symbol ?? currencyCode];
  }, [currencyCode, precision, locale]);

  return {
    sumFormatter,
    currencySymbol,
    currencyCode,
    precision,
    formatCents: (cents: number) => {
      return sumFormatter.format(centsToValue(cents, precision));
    },
  };
}
